import { AssetInventoryType, AssetLookup } from 'models'
import { AssetInventoryServiceV4 } from 'services'

type Listener = (lookup: AssetLookup) => void

interface Entry {
  lookup: AssetLookup
  nextPage: number
  loading: boolean
  listeners: Set<Listener>
}

const EMPTY: AssetLookup = { available: true, values: [], hasNoValue: false, complete: false }

/**
 * Filter values are a property of the nightly build, not of the current query, so they are cached
 * for the session and shared by every dropdown that asks for the same column.
 */
const cache = new Map<string, Entry>()

const keyFor = (assetType: AssetInventoryType, columnName: string) => `${assetType}:${columnName}`

const entryFor = (key: string): Entry => {
  const existing = cache.get(key)

  if (existing) return existing

  const created: Entry = { lookup: EMPTY, nextPage: 1, loading: false, listeners: new Set() }
  cache.set(key, created)

  return created
}

const emit = (entry: Entry) => entry.listeners.forEach((listener) => listener(entry.lookup))

/* One page at a time so the dropdown can paint page 1 while the rest is still arriving. */
const readPage = async (entry: Entry, assetType: AssetInventoryType, columnName: string): Promise<void> => {
  const page = await AssetInventoryServiceV4.getLookupPage(assetType, columnName, entry.nextPage)

  if (!page.available) {
    entry.lookup = { available: false, values: [], hasNoValue: false, complete: true }

    return
  }

  entry.lookup = {
    available: true,
    values: entry.lookup.values.concat(page.values),
    hasNoValue: entry.lookup.hasNoValue || page.hasNoValue,
    complete: !page.hasMore
  }
  entry.nextPage += 1
}

const drain = async (entry: Entry, assetType: AssetInventoryType, columnName: string): Promise<void> => {
  if (entry.loading || entry.lookup.complete) return

  entry.loading = true

  try {
    while (!entry.lookup.complete) {
      await readPage(entry, assetType, columnName)
      emit(entry)
    }
  } catch (error) {
    /* Left incomplete on purpose: reopening the dropdown resumes from the page that failed. */
    console.error(`Failed to load filter values for ${columnName}:`, error)
  } finally {
    entry.loading = false
    emit(entry)
  }
}

/**
 * Subscribes to a column's values and keeps fetching until every page is in. The listener fires
 * once per page, so callers render a growing list rather than waiting for the whole column —
 * `MAC_ADDRESSES` runs to ~987,000 distinct values on the largest tenant.
 */
export const loadAssetLookup = (
  assetType: AssetInventoryType,
  columnName: string,
  onProgress: Listener
): (() => void) => {
  const entry = entryFor(keyFor(assetType, columnName))

  entry.listeners.add(onProgress)
  onProgress(entry.lookup)
  drain(entry, assetType, columnName)

  return () => entry.listeners.delete(onProgress)
}

/* Page one only, for callers that need to know whether a column has anything to offer. */
export const primeAssetLookup = async (assetType: AssetInventoryType, columnName: string): Promise<string[]> => {
  const entry = entryFor(keyFor(assetType, columnName))

  if (!entry.lookup.values.length && !entry.lookup.complete && !entry.loading) {
    entry.loading = true

    try {
      await readPage(entry, assetType, columnName)
    } finally {
      entry.loading = false
      emit(entry)
    }
  }

  return entry.lookup.values
}

export const clearAssetLookups = () => cache.clear()
